import {
  GenerateNotaryEmailDraftRequestBody,
  NotaryEmailLegalParty,
  NotaryEmailRepresentation,
} from '@inversor-hub/types';

export interface NotaryPartyFormValue {
  fullName: string;
  documentType: string;
  documentNumber: string;
  address: string;
  civilStatus: string;
  profession: string;
}

export interface NotaryRepresentationFormValue {
  representativeName: string;
  representedName: string;
  authority: '' | 'POWER_OF_ATTORNEY' | 'OTHER';
  details: string;
}

export interface NotaryPartyGroupFormValue {
  parties: NotaryPartyFormValue[];
  relationshipStatement: string;
  representations: NotaryRepresentationFormValue[];
}

export interface NotaryEmailDraftFormValues {
  senderFullName: string;
  senderPhone: string;
  transparencyActDate: string;
  saleDeedDate: string;
  sellers: NotaryPartyGroupFormValue;
  buyers: NotaryPartyGroupFormValue;
  propertyDescription: string;
  propertyAddress: string;
  cadastralReference: string;
  registryOffice: string;
  registryPropertyNumber: string;
  registryVolume: string;
  registryBook: string;
  registryFolio: string;
  registryEntry: string;
  totalPriceEuros: string;
  earnestMoneyAmountEuros: string;
  transferAmountEuros: string;
  notaryCosts: '' | 'BUYER' | 'SELLER' | 'SHARED' | 'OTHER';
  notaryCostsDetails: string;
  propertyTaxes: '' | 'PRORATED' | 'BUYER' | 'SELLER' | 'OTHER';
  propertyTaxesDetails: string;
  earnestMoneyProofMediaIds: string[];
  powersStatus: '' | 'NONE' | 'EXISTS' | 'UNKNOWN';
  powersDetails: string;
  mortgageBank: string;
  mortgageFein: string;
  communityStatus: '' | 'CONSTITUTED' | 'NOT_CONSTITUTED';
  communityDetails: string;
  energyCertificateStatus: '' | 'AVAILABLE' | 'PENDING' | 'NOT_APPLICABLE';
  energyCertificateAvailableOn: string;
  energyCertificateDetails: string;
  documentNotes: string;
  additionalNotes: string;
}

export const emptyNotaryParty = (): NotaryPartyFormValue => ({
  fullName: '',
  documentType: '',
  documentNumber: '',
  address: '',
  civilStatus: '',
  profession: '',
});

export const emptyNotaryRepresentation = (): NotaryRepresentationFormValue => ({
  representativeName: '',
  representedName: '',
  authority: '',
  details: '',
});

export const createNotaryEmailDraftFormValues = (): NotaryEmailDraftFormValues => ({
  senderFullName: '',
  senderPhone: '',
  transparencyActDate: '',
  saleDeedDate: '',
  sellers: {
    parties: [],
    relationshipStatement: '',
    representations: [],
  },
  buyers: {
    parties: [],
    relationshipStatement: '',
    representations: [],
  },
  propertyDescription: '',
  propertyAddress: '',
  cadastralReference: '',
  registryOffice: '',
  registryPropertyNumber: '',
  registryVolume: '',
  registryBook: '',
  registryFolio: '',
  registryEntry: '',
  totalPriceEuros: '',
  earnestMoneyAmountEuros: '',
  transferAmountEuros: '',
  notaryCosts: '',
  notaryCostsDetails: '',
  propertyTaxes: '',
  propertyTaxesDetails: '',
  earnestMoneyProofMediaIds: [],
  powersStatus: '',
  powersDetails: '',
  mortgageBank: '',
  mortgageFein: '',
  communityStatus: '',
  communityDetails: '',
  energyCertificateStatus: '',
  energyCertificateAvailableOn: '',
  energyCertificateDetails: '',
  documentNotes: '',
  additionalNotes: '',
});

const text = (value: string): string | undefined => value.trim() || undefined;

const cents = (value: string): number | undefined => {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : undefined;
};

const party = (value: NotaryPartyFormValue): NotaryEmailLegalParty | undefined => {
  const result: NotaryEmailLegalParty = {
    fullName: text(value.fullName),
    documentType: text(value.documentType),
    documentNumber: text(value.documentNumber),
    address: text(value.address),
    civilStatus: text(value.civilStatus),
    profession: text(value.profession),
  };
  return Object.values(result).some(Boolean) ? result : undefined;
};

const representation = (
  value: NotaryRepresentationFormValue
): NotaryEmailRepresentation | undefined => {
  const result: NotaryEmailRepresentation = {
    representativeName: text(value.representativeName),
    representedName: text(value.representedName),
    authority: value.authority || undefined,
    details: text(value.details),
  };
  return Object.values(result).some(Boolean) ? result : undefined;
};

const partyGroup = (value: NotaryPartyGroupFormValue) => {
  const parties = value.parties.map(party).filter((item): item is NotaryEmailLegalParty => !!item);
  const representations = value.representations
    .map(representation)
    .filter((item): item is NotaryEmailRepresentation => !!item);
  const relationshipStatement = text(value.relationshipStatement);

  if (!parties.length && !representations.length && !relationshipStatement) return undefined;
  return {
    parties: parties.length ? parties : undefined,
    relationshipStatement,
    representations: representations.length ? representations : undefined,
  };
};

export const toNotaryEmailDraftRequest = (
  values: NotaryEmailDraftFormValues
): GenerateNotaryEmailDraftRequestBody => {
  const registry = {
    office: text(values.registryOffice),
    propertyNumber: text(values.registryPropertyNumber),
    volume: text(values.registryVolume),
    book: text(values.registryBook),
    folio: text(values.registryFolio),
    entry: text(values.registryEntry),
  };
  const hasRegistry = Object.values(registry).some(Boolean);
  const earnestMoney = {
    amountCents: cents(values.earnestMoneyAmountEuros),
    proofMediaIds: values.earnestMoneyProofMediaIds.length
      ? values.earnestMoneyProofMediaIds
      : undefined,
  };
  const hasEarnestMoney = Object.values(earnestMoney).some(Boolean);
  const property = {
    description: text(values.propertyDescription),
    address: text(values.propertyAddress),
    cadastralReference: text(values.cadastralReference),
    registry: hasRegistry ? registry : undefined,
  };
  const hasProperty = Object.values(property).some(Boolean);
  const totalPriceCents = cents(values.totalPriceEuros);
  const transferAmountCents = cents(values.transferAmountEuros);
  const notes = values.additionalNotes
    .split('\n')
    .map((note) => note.trim())
    .filter(Boolean);
  const documentNotes = values.documentNotes
    .split('\n')
    .map((note) => note.trim())
    .filter(Boolean);

  return {
    sender:
      text(values.senderFullName) || text(values.senderPhone)
        ? {
            fullName: text(values.senderFullName),
            phone: text(values.senderPhone),
          }
        : undefined,
    dates:
      text(values.transparencyActDate) || text(values.saleDeedDate)
        ? {
            transparencyAct: text(values.transparencyActDate),
            saleDeed: text(values.saleDeedDate),
          }
        : undefined,
    sellers: partyGroup(values.sellers),
    buyers: partyGroup(values.buyers),
    property: hasProperty ? property : undefined,
    transaction:
      totalPriceCents ||
      transferAmountCents ||
      hasEarnestMoney ||
      values.notaryCosts ||
      text(values.notaryCostsDetails) ||
      values.propertyTaxes ||
      text(values.propertyTaxesDetails)
        ? {
            totalPriceCents,
            transferAmountCents,
            notaryCosts: values.notaryCosts || undefined,
            notaryCostsDetails: text(values.notaryCostsDetails),
            propertyTaxes: values.propertyTaxes || undefined,
            propertyTaxesDetails: text(values.propertyTaxesDetails),
            earnestMoney: hasEarnestMoney ? earnestMoney : undefined,
          }
        : undefined,
    powers:
      values.powersStatus || text(values.powersDetails)
        ? {
            status: values.powersStatus || undefined,
            details: text(values.powersDetails),
          }
        : undefined,
    mortgage:
      text(values.mortgageBank) || text(values.mortgageFein)
        ? {
            bank: text(values.mortgageBank),
            fein: text(values.mortgageFein),
          }
        : undefined,
    community:
      values.communityStatus || text(values.communityDetails)
        ? {
            status: values.communityStatus || undefined,
            details: text(values.communityDetails),
          }
        : undefined,
    energyCertificate:
      values.energyCertificateStatus ||
      values.energyCertificateAvailableOn ||
      text(values.energyCertificateDetails)
        ? {
            status: values.energyCertificateStatus || undefined,
            availableOn: values.energyCertificateAvailableOn || undefined,
            details: text(values.energyCertificateDetails),
          }
        : undefined,
    documentNotes: documentNotes.length ? documentNotes : undefined,
    additionalNotes: notes.length ? notes : undefined,
  };
};
